import { reportBookingAttemptBlocked } from '@wix/ambassador-bookings-automations-v2-automation/http';
import { missedBooking } from '@wix/ambassador-bookings-notifications-v1-notifications-settings/http';
import { listDataExtensionSchemas } from '@wix/ambassador-data-extensions-v1-data-extension-schema/http';
import type { DataExtensionSchema } from '@wix/ambassador-data-extensions-v1-data-extension-schema/types';
import {
  queryCategories,
  queryLocations,
  queryServices,
  searchServices,
} from '@wix/ambassador-bookings-services-v2-service/http';
import {
  LocationType,
  QueryServicesRequest,
  QueryServicesResponse,
  RequestedFields,
  Service,
  SortOrder,
  V2Category,
} from '@wix/ambassador-bookings-services-v2-service/types';
import { getBookingsSettings } from '@wix/ambassador-bookings-v2-bookings-settings/http';
import { RequestedFields as RequestedFieldsBookingsSettings } from '@wix/ambassador-bookings-v2-bookings-settings/types';
import {
  ListSchedulesRequest,
  Schedule,
  ScheduleServer,
  ScheduleStatus,
} from '@wix/ambassador-schedule-server/http';
import { isServiceConnectedToPricingPlan } from '@wix/bookings-calendar-catalog-viewer-mapper';
import {
  BOOKINGS_APP_DEF_ID,
  getInstance,
  getServerBaseUrl,
  manageWarmupData,
} from '@wix/bookings-catalog-calendar-viewer-utils';
import { WeekDay } from '@wix/bookings-uou-domain';
import { ReservedLocationIds } from '@wix/bookings-uou-types';
import { ControllerFlowAPI } from '@wix/yoshi-flow-editor';
import { ServiceListSettings } from '../../legacy/appSettings/appSettings';
import { ALL_SERVICES, BOOKINGS_FES_BASE_DOMAIN } from '../consts';
import { WixCalendarEventsV3Event } from '../gql/graphql';
import { FilterServicesByOptions } from '../types/types';
import { getFilterServicesBy } from '../utils/filterOptions/getFilterOptions';
import { buildQueryServicesFilter } from '../utils/filters/buildQueryServicesFilter';
import {
  getRequestedPageFromQueryParam,
  getServicesPerPage,
} from '../utils/pagination/pagination';
import { PanoramaTransactions } from '../utils/panorama/consts';
import { AutoGraphqlApi } from './AutoGraphqlApi';

export const enum pricingPlanConst {
  PAGE_NOT_INSTALLED = 'PageNotInstalled',
  NO_PLANS_ASSIGNED_TO_OFFERING = 'NoPlansAssignedToOffering',
}

type QueryServicesRequestQuery = QueryServicesRequest['query'];
type QueryServicesRequestFilters = QueryServicesRequestQuery['filter'];
type QueryServicesRequestPaging = QueryServicesRequestQuery['paging'];

export type QueryServicesFilter = {
  staffMemberIds?: string[];
  categoryIds?: string[];
  serviceIds?: string[];
  locationIds?: (string | typeof ReservedLocationIds.OTHER_LOCATIONS)[];
  limit?: number;
};

export type ServiceAvailabilityMap = {
  [id: string]: { spotsLeft: number };
};

export const CATALOG_SERVER_URL = '_api/services-catalog';
const SCHEDULE_SERVER_URL = '_api/schedule-reader-server';
const XSRF_HEADER_NAME = 'X-XSRF-TOKEN';
const REVISION_HEADER_NAME = 'x-wix-site-revision';

const SCHEDULES_REQUEST = 100;

export class BookingsAPI {
  private static cache = new Map();
  private flowAPI: ControllerFlowAPI;
  private appSettings: ServiceListSettings;
  private shouldWorkWithAppSettings: boolean;
  private scheduleServer: ReturnType<typeof ScheduleServer>;
  private autoGraphQlAPI: AutoGraphqlApi;

  constructor({
    appSettings,
    flowAPI,
    shouldWorkWithAppSettings,
  }: {
    flowAPI: ControllerFlowAPI;
    appSettings: ServiceListSettings;
    shouldWorkWithAppSettings: boolean;
  }) {
    const serverBaseUrl = getServerBaseUrl({
      wixCodeApi: flowAPI.controllerConfig.wixCodeApi,
      appParams: flowAPI.controllerConfig.appParams,
    });

    this.flowAPI = flowAPI;
    this.appSettings = appSettings;
    this.shouldWorkWithAppSettings = shouldWorkWithAppSettings;
    this.scheduleServer = ScheduleServer(
      `${serverBaseUrl}${SCHEDULE_SERVER_URL}`,
    );

    this.autoGraphQlAPI = new AutoGraphqlApi({ flowAPI });
  }

  static clearCache() {
    BookingsAPI.cache.clear();
  }

  private get authorization() {
    return getInstance({
      wixCodeApi: this.flowAPI.controllerConfig.wixCodeApi,
      appParams: this.flowAPI.controllerConfig.appParams,
    });
  }

  private async withCache<T>(
    key: string,
    request: () => Promise<T> | T,
    {
      useWarmupData,
      onClientFetch,
    }: { useWarmupData?: boolean; onClientFetch?: (data: T) => void } = {},
  ): Promise<T> {
    const cachedResult = BookingsAPI.cache.get(key);

    if (cachedResult) {
      return cachedResult;
    }

    const result = useWarmupData
      ? await manageWarmupData(request, key, this.flowAPI, {
          onClientFetch: (data: T) => {
            BookingsAPI.cache.set(key, data);
            onClientFetch?.(data);
          },
        })
      : await request();
    BookingsAPI.cache.set(key, result);

    return result;
  }

  private buildQueryServicesFilterPayload({
    categoryIds,
    locationIds,
    serviceIds,
    staffMemberIds,
    selectedFilterOptionId,
  }: QueryServicesFilter & { selectedFilterOptionId?: string }) {
    let filter: QueryServicesRequestFilters = {
      appId: BOOKINGS_APP_DEF_ID,
    };
    const hiddenFilter = {
      $or: [{ hidden: false }, { hidden: { $exists: false } }],
    };
    const orFilters: QueryServicesRequestFilters[] = [hiddenFilter];

    if (staffMemberIds) {
      filter.staffMemberIds = {
        $hasSome: staffMemberIds,
      };
    }

    const serviceIdsFilter = {
      id: {
        $in: serviceIds,
      },
    };
    const categoryIdsFilter = {
      'category.id': {
        $in: categoryIds,
      },
    };
    if (serviceIds && categoryIds) {
      const filterServicesBy = getFilterServicesBy(
        this.appSettings,
        this.flowAPI.settings,
      );
      const isInSpecificCategoryTab =
        selectedFilterOptionId &&
        selectedFilterOptionId !== ALL_SERVICES &&
        filterServicesBy === FilterServicesByOptions.CATEGORIES;

      const operator = isInSpecificCategoryTab ? '$and' : '$or';

      orFilters.push({
        [operator]: [serviceIdsFilter, categoryIdsFilter],
      });
    } else if (serviceIds) {
      filter = {
        ...filter,
        ...serviceIdsFilter,
      };
    } else if (categoryIds) {
      filter = {
        ...filter,
        ...categoryIdsFilter,
      };
    }

    if (locationIds) {
      const includeNonBusinessLocations = locationIds.includes(
        ReservedLocationIds.OTHER_LOCATIONS,
      );
      const businessLocationIds = locationIds.filter(
        (locationId) => locationId !== ReservedLocationIds.OTHER_LOCATIONS,
      );

      const otherLocationsFilter = {
        'locations.type': {
          $hasSome: [LocationType.CUSTOM, LocationType.CUSTOMER],
        },
      };
      const businessLocationFilter = {
        'locations.business.id': { $hasSome: businessLocationIds },
      };

      if (includeNonBusinessLocations && businessLocationIds.length > 0) {
        orFilters.push({ $or: [businessLocationFilter, otherLocationsFilter] });
      } else if (includeNonBusinessLocations) {
        filter = { ...filter, ...otherLocationsFilter };
      } else {
        filter = { ...filter, ...businessLocationFilter };
      }
    }

    if (orFilters.length > 1) {
      filter.$and = orFilters;
    } else {
      const [f] = orFilters;
      filter.$or = (f as any).$or;
    }
    return filter;
  }

  async queryServices({
    selectedFilterOptionId,
    onClientFetch,
  }: {
    selectedFilterOptionId?: string;
    onClientFetch?: (data: QueryServicesResponse) => void;
  } = {}): Promise<QueryServicesResponse> {
    const page = getRequestedPageFromQueryParam(
      this.flowAPI.controllerConfig.wixCodeApi,
    );

    const servicesPerPage = getServicesPerPage(this.flowAPI);
    const offset = (page - 1) * servicesPerPage;
    const filters = buildQueryServicesFilter({
      flowAPI: this.flowAPI,
      shouldWorkWithAppSettings: this.shouldWorkWithAppSettings,
      appSettings: this.appSettings,
      selectedFilterOptionId,
    });

    const sort = [
      {
        fieldName: 'category.sortOrder',
        order: SortOrder.ASC,
      },
      {
        fieldName: 'sortOrder',
        order: SortOrder.ASC,
      },
    ];

    const paging: QueryServicesRequestPaging = {
      limit: filters.limit || servicesPerPage,
      offset,
    };

    const panoramaTransaction = this.flowAPI.panoramaClient.transaction(
      PanoramaTransactions.serviceListServicesQuery,
    );
    panoramaTransaction.start({
      customParams: {
        selectedFilterOptionId,
        limit: paging.limit,
        offset: paging.offset,
        hasServiceIdsFilter: Boolean(filters.serviceIds?.length),
        hasCategoryFilter: Boolean(filters.categoryIds?.length),
        hasStaffFilter: Boolean(filters.staffMemberIds?.length),
        hasLocationFilter: Boolean(filters.locationIds?.length),
      },
    });

    const response = await this.withCache(
      `queryServices-${JSON.stringify(filters)}-limit:${paging.limit}-offset:${
        paging.offset
      }`,
      () =>
        this.flowAPI.httpClient
          .request(
            queryServices({
              conditionalFields: [
                RequestedFields.STAFF_MEMBER_DETAILS,
                RequestedFields.DISCOUNT_INFO_DETAILS,
              ],
              query: {
                sort,
                paging,
                filter: this.buildQueryServicesFilterPayload({
                  ...filters,
                  selectedFilterOptionId,
                }),
              },
            }),
          )
          .then((queryServicesResponse) => queryServicesResponse.data),
      { useWarmupData: true, onClientFetch },
    );

    panoramaTransaction.finish({});

    response.services?.forEach((service) => {
      if (service.schedule) {
        service.schedule.firstSessionStart = convertWarmupDateStringToDate(
          service.schedule?.firstSessionStart!,
        );
        service.schedule.lastSessionEnd = convertWarmupDateStringToDate(
          service.schedule?.lastSessionEnd!,
        );
      }
    });

    return response;
  }

  async getBookingsSettings() {
    const cacheKey = 'getBookingsSettings';
    const panoramaTransaction = this.flowAPI.panoramaClient.transaction(
      PanoramaTransactions.serviceListBookingsSettingsFetch,
    );
    panoramaTransaction.start({});
    const response = await this.withCache(
      cacheKey,
      async () => {
        const httpResponse = await this.flowAPI.httpClient.request(
          getBookingsSettings({
            fields: [
              RequestedFieldsBookingsSettings.SITE_PROPERTIES,
              RequestedFieldsBookingsSettings.FEATURES,
            ],
          }),
        );
        return httpResponse.data;
      },
      { useWarmupData: true },
    );
    panoramaTransaction.finish({});
    return response;
  }

  async notifyOwnerNonPremiumEnrollmentAttempt({
    service,
  }: {
    service: Service;
  }) {
    if (!service.id) {
      return {};
    }
    return this.flowAPI.httpClient
      .request(
        reportBookingAttemptBlocked({
          serviceId: service.id,
        }),
      )
      .catch(() => ({}));
  }

  notifyOwnerNonPricingPlanEnrollmentAttempt({
    isPricingPlanInstalled,
    service,
  }: {
    service: Service;
    isPricingPlanInstalled: boolean;
  }) {
    const reasons: pricingPlanConst[] = [];
    const offeringId = service.id;

    if (!isPricingPlanInstalled) {
      reasons.push(pricingPlanConst.PAGE_NOT_INSTALLED);
    }
    if (!isServiceConnectedToPricingPlan({ payment: service.payment! })) {
      reasons.push(pricingPlanConst.NO_PLANS_ASSIGNED_TO_OFFERING);
    }

    return this.flowAPI.httpClient.post(
      `${BOOKINGS_FES_BASE_DOMAIN}/pricing-plans/invalidSetup`,
      { offeringId, reasons },
      {
        headers: {
          'Content-Type': 'application/json',
          [REVISION_HEADER_NAME]:
            this.flowAPI.controllerConfig.wixCodeApi.site.revision,
          [XSRF_HEADER_NAME]:
            this.flowAPI.controllerConfig.platformAPIs.getCsrfToken(),
        },
      },
    );
  }

  async getEventsOfferedDays(scheduleIds: string[]) {
    const panoramaTransaction = this.flowAPI.panoramaClient.transaction(
      PanoramaTransactions.serviceListClassesDaysEnrichment,
    );
    panoramaTransaction.start({
      customParams: {
        numberOfScheduleIds: scheduleIds.length,
      },
    });
    const result = await this.withCache(
      `getEventsOfferedDays-${scheduleIds.join(',')}`,
      async () => {
        const { data: response } =
          await this.autoGraphQlAPI.queryEventsClassDays({
            scheduleIds,
          });

        const recurringEvents =
          (response?.wixCalendarEventsV3Events
            ?.items as WixCalendarEventsV3Event[]) || [];

        return recurringEvents.reduce<Record<string, WeekDay[]>>(
          (acc, item) => {
            const { scheduleId, recurrenceRule } = item;
            acc[scheduleId!] = acc[scheduleId!] || [];
            acc[scheduleId!].push(
              recurrenceRule?.days?.[0]! as unknown as WeekDay,
            );
            return acc;
          },
          {},
        );
      },
    );
    panoramaTransaction.finish({});
    return result;
  }

  async getServicesAvailability(serviceIds: string[]) {
    const panoramaTransaction = this.flowAPI.panoramaClient.transaction(
      PanoramaTransactions.serviceListAvailabilityEnrichment,
    );
    panoramaTransaction.start({
      customParams: {
        numberOfServiceIds: serviceIds.length,
      },
    });
    const result = await this.withCache(
      `getServicesAvailability-${serviceIds.join(',')}`,
      async () => {
        const schedulesService = this.scheduleServer.Schedules();
        let schedules: Schedule[] = [];

        for (let i = 0; i < serviceIds.length; i += SCHEDULES_REQUEST) {
          const partialScheduleOwnerIds = serviceIds.slice(
            i,
            i + SCHEDULES_REQUEST,
          );
          const listSchedulesRequest: ListSchedulesRequest = {
            scheduleOwnerIds: partialScheduleOwnerIds,
            includeTotalNumberOfParticipants: true,
          };
          const { schedules: partialSchedules } = await schedulesService({
            authorization: this.authorization,
          }).list(listSchedulesRequest);
          schedules = [...schedules, ...(partialSchedules || [])];
        }

        return schedules.reduce<ServiceAvailabilityMap>((acc, s) => {
          if (s.status === ScheduleStatus.CREATED) {
            acc[s?.scheduleOwnerId!] = {
              spotsLeft: s?.capacity! - s?.totalNumberOfParticipants!,
            };
          }
          return acc;
        }, {});
      },
    );
    panoramaTransaction.finish({});
    return result;
  }

  async queryCategories(): Promise<V2Category[] | undefined> {
    const filters = buildQueryServicesFilter({
      flowAPI: this.flowAPI,
      shouldWorkWithAppSettings: this.shouldWorkWithAppSettings,
      appSettings: this.appSettings,
      ignorePreSelectedCategories: true,
    });
    const filterPayload = this.buildQueryServicesFilterPayload(filters);
    const panoramaTransaction = this.flowAPI.panoramaClient.transaction(
      PanoramaTransactions.serviceListCategoriesQuery,
    );
    panoramaTransaction.start({
      customParams: {
        filterPayload: JSON.stringify(filterPayload),
      },
    });
    const categories = await this.withCache(
      `queryCategories-${JSON.stringify(filters)}`,
      () =>
        this.flowAPI.httpClient
          .request(
            queryCategories({
              filter: {
                services: filterPayload,
                categoryIds: filters.categoryIds,
              },
            }),
          )
          .then((response) => response.data.categories),
      { useWarmupData: true },
    );
    panoramaTransaction.finish({});
    return categories;
  }

  async queryLocations() {
    const filters = buildQueryServicesFilter({
      flowAPI: this.flowAPI,
      shouldWorkWithAppSettings: this.shouldWorkWithAppSettings,
      appSettings: this.appSettings,
      ignorePreSelectedLocations: true,
    });
    const filterPayload = this.buildQueryServicesFilterPayload(filters);
    const panoramaTransaction = this.flowAPI.panoramaClient.transaction(
      PanoramaTransactions.serviceListLocationsQuery,
    );
    panoramaTransaction.start({
      customParams: {
        filterPayload: JSON.stringify(filterPayload),
      },
    });
    const locations = await this.withCache(
      `queryLocations-${filters}`,
      () =>
        this.flowAPI.httpClient
          .request(
            queryLocations({
              filter: {
                services: filterPayload,
                businessLocationIds: filters.locationIds?.filter(
                  (id) => id !== ReservedLocationIds.OTHER_LOCATIONS,
                ),
              },
            }),
          )
          .then((response) => response.data),
      {
        useWarmupData: true,
      },
    );
    panoramaTransaction.finish({});
    return locations;
  }

  async listDataExtensionSchemas(): Promise<DataExtensionSchema[]> {
    const response = await this.flowAPI.httpClient.request(
      listDataExtensionSchemas({ fqdn: 'wix.bookings.services.v2.service' }),
    );
    return response.data.dataExtensionSchemas ?? [];
  }

  async searchServices(filter: Record<string, any>): Promise<Service[]> {
    const response = await this.flowAPI.httpClient.request(
      searchServices({
        search: { filter: { ...filter, appId: BOOKINGS_APP_DEF_ID } },
      }),
    );
    return response.data.services ?? [];
  }
}

const convertWarmupDateStringToDate = (
  stringDate: string | Date | undefined | null,
) => (stringDate && new Date(stringDate)) as Date;
